import { useState, useEffect, useRef } from 'react';
import { adminGetTickets, adminGetTicket, adminReplyTicket, adminUpdateTicketStatus } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  MessageCircle, ArrowLeft, Send, Loader2, Clock, CheckCircle, AlertCircle, XCircle, User, Shield, Filter, Inbox, Bot
} from 'lucide-react';

const statusConfig = {
  open: { label: 'Ouvert', color: 'bg-blue-100 text-blue-700', icon: Clock },
  in_progress: { label: 'En cours', color: 'bg-amber-100 text-amber-700', icon: AlertCircle },
  resolved: { label: 'Resolu', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  closed: { label: 'Ferme', color: 'bg-slate-100 text-slate-600', icon: XCircle },
};

const priorityConfig = {
  low: { label: 'Basse', color: 'bg-slate-100 text-slate-600' },
  medium: { label: 'Moyenne', color: 'bg-blue-100 text-blue-600' },
  high: { label: 'Haute', color: 'bg-red-100 text-red-600' },
};

const categoryLabels = {
  technical: 'Technique', billing_question: 'Facturation', feature_request: 'Fonctionnalite', subscription: 'Abonnement', other: 'Autre',
};

const AdminTicketsPage = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [replyStatus, setReplyStatus] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => { loadTickets(); }, [filterStatus, filterPriority]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [selectedTicket?.messages]);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterPriority) params.priority = filterPriority;
      const res = await adminGetTickets(params);
      setTickets(res.data);
    } catch (err) { console.error('Failed to load tickets:', err); } finally { setLoading(false); }
  };

  const handleOpenTicket = async (ticketId) => {
    try {
      const res = await adminGetTicket(ticketId);
      setSelectedTicket(res.data);
      setReplyStatus(res.data.status);
      loadTickets();
    } catch { toast.error('Erreur chargement'); }
  };

  const handleReply = async () => {
    if (!replyText.trim()) return;
    setReplying(true);
    try {
      await adminReplyTicket(selectedTicket.id, { message: replyText, status: replyStatus });
      setReplyText('');
      const res = await adminGetTicket(selectedTicket.id);
      setSelectedTicket(res.data);
      loadTickets();
      toast.success('Reponse envoyee');
    } catch { toast.error('Erreur envoi'); } finally { setReplying(false); }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await adminUpdateTicketStatus(selectedTicket.id, { status: newStatus });
      setReplyStatus(newStatus);
      const res = await adminGetTicket(selectedTicket.id);
      setSelectedTicket(res.data);
      loadTickets();
      toast.success('Statut mis a jour');
    } catch { toast.error('Erreur'); }
  };

  const StatusBadge = ({ status }) => {
    const cfg = statusConfig[status] || statusConfig.open;
    return <Badge className={`${cfg.color} hover:${cfg.color} text-xs`}>{cfg.label}</Badge>;
  };

  // DETAIL VIEW
  if (selectedTicket) return (
    <div className="space-y-4" data-testid="admin-ticket-detail">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => { setSelectedTicket(null); loadTickets(); }}><ArrowLeft className="w-5 h-5" /></Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-white truncate">{selectedTicket.subject}</h1>
          <p className="text-xs text-zinc-500">{selectedTicket.user_email} - {selectedTicket.company_name} - {categoryLabels[selectedTicket.category]}</p>
        </div>
        <Select value={replyStatus} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-36" data-testid="admin-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Ouvert</SelectItem>
            <SelectItem value="in_progress">En cours</SelectItem>
            <SelectItem value="resolved">Resolu</SelectItem>
            <SelectItem value="closed">Ferme</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-2">
        {selectedTicket.messages?.map(msg => (
          <div key={msg.id} className={`flex ${msg.sender === 'admin' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[75%] rounded-xl px-4 py-3 ${
              msg.sender === 'admin' ? 'bg-emerald-600/20 border border-emerald-500/30' :
              msg.sender === 'ai' ? 'bg-violet-600/15 border border-violet-500/30' :
              'bg-zinc-800 border border-zinc-700'
            }`}>
              <div className="flex items-center gap-2 mb-1">
                {msg.sender === 'admin' ? <Shield className="w-3 h-3 text-emerald-400" /> :
                 msg.sender === 'ai' ? <Bot className="w-3 h-3 text-violet-400" /> :
                 <User className="w-3 h-3 text-blue-400" />}
                <span className="text-xs font-medium text-zinc-400">{msg.sender_name}</span>
                <span className="text-xs text-zinc-600">{new Date(msg.created_at).toLocaleString('fr-CH')}</span>
              </div>
              <p className="text-sm text-zinc-200 whitespace-pre-wrap">{msg.content}</p>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="flex gap-2 pt-2 border-t border-zinc-800">
        <Input value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Repondre au client..." className="flex-1"
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleReply(); }}}
          data-testid="admin-reply-input" />
        <Button onClick={handleReply} disabled={replying || !replyText.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="admin-reply-btn">
          {replying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );

  // LIST VIEW
  return (
    <div className="space-y-6" data-testid="admin-tickets-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Tickets Support</h1>
          <p className="text-sm text-zinc-400 mt-1">{tickets.length} ticket(s)</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filterStatus} onValueChange={v => setFilterStatus(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-32"><Filter className="w-3 h-3 mr-1" /><SelectValue placeholder="Statut" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="open">Ouvert</SelectItem>
              <SelectItem value="in_progress">En cours</SelectItem>
              <SelectItem value="resolved">Resolu</SelectItem>
              <SelectItem value="closed">Ferme</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterPriority} onValueChange={v => setFilterPriority(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-32"><SelectValue placeholder="Priorite" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              <SelectItem value="high">Haute</SelectItem>
              <SelectItem value="medium">Moyenne</SelectItem>
              <SelectItem value="low">Basse</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
      ) : tickets.length === 0 ? (
        <Card className="border-dashed border-zinc-700 bg-zinc-900/50">
          <CardContent className="py-12 text-center">
            <Inbox className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
            <h3 className="text-lg font-medium text-white">Aucun ticket</h3>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {tickets.map(t => (
            <button key={t.id} onClick={() => handleOpenTicket(t.id)}
              className={`w-full text-left p-4 rounded-lg border transition-colors ${t.unread_by_admin ? 'bg-emerald-950/30 border-emerald-500/40' : 'bg-zinc-900/50 border-zinc-800 hover:border-zinc-700'}`}
              data-testid={`admin-ticket-${t.id}`}>
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-medium text-white truncate text-sm">{t.subject}</p>
                    {t.unread_by_admin && <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />}
                  </div>
                  <p className="text-xs text-zinc-500">{t.user_email} - {t.company_name} - {new Date(t.updated_at).toLocaleDateString('fr-CH')}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Badge className={`${priorityConfig[t.priority]?.color} text-xs`}>{priorityConfig[t.priority]?.label}</Badge>
                  <StatusBadge status={t.status} />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminTicketsPage;
